import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { CustomPodOptions, Pod, Region } from '@partiri-cloud/types';

/**
 * @file resources.ts
 * Class for the projects client
 */
export class ResourcesApi {
  protected endpoint = '/resources'
  
  constructor(private http: HttpClient) { }

  /**
   * List all available regions for a workspace
   * @returns Region[]
   */
  public listRegions(workspaceId: string): Observable<Region[]> {
    return this.http.get<Region[]>(`${this.endpoint}/regions`, { params: { workspace: workspaceId } })
  }

  /**
   * List all available pods for a workspace
   * @returns Pod[]
   */
  public listPods(workspaceId: string): Observable<Pod[]> {
    return this.http.get<Pod[]>(`${this.endpoint}/pods`, { params: { workspace: workspaceId } })
  }

  /**
   * Prices for a region. The response covers the CATALOGUE pods only; a custom
   * pod is not in it, so its id must be named in `podIds` or it comes back
   * absent and every caller's `?? 0` fallback quotes a paid pod as free.
   * @param regionId
   * @param podIds extra pod ids to price on top of the catalogue
   */
  public getPricing(regionId: string, podIds: string[] = []): Observable<{ pods: { fk_pod: string; price: number; perMinute: number }[]; volume_price_per_gb: number }> {
    const params: Record<string, string> = { region: regionId }
    if (podIds.length > 0) params['pods'] = podIds.join(',')
    return this.http.get<{ pods: { fk_pod: string; price: number; perMinute: number }[]; volume_price_per_gb: number }>(`${this.endpoint}/pricing`, { params })
  }

  /**
   * Read one pod class by id, including a custom one. `listPods` returns the
   * catalogue only, so a service running a custom pod has an fk_pod that
   * appears nowhere in that list — this is how the UI resolves its spec.
   */
  public readPod(podId: string): Observable<Pod> {
    return this.http.get<Pod>(`${this.endpoint}/pods/${podId}`)
  }

  /**
   * Slider bounds and rate card for a custom pod across the given regions.
   * `available: false` means the custom option must be hidden rather than
   * offered — the server would reject anything picked from it.
   */
  public getCustomPodOptions(regionIds: string[]): Observable<CustomPodOptions> {
    return this.http.get<CustomPodOptions>(`${this.endpoint}/custom-pod`, { params: { regions: regionIds.join(',') } })
  }

  /**
   * List the public ingress IP addresses for a region — the DNS A-record
   * targets a custom domain should point at.
   * @param regionId
   */
  public listIngressIps(regionId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.endpoint}/ingress-ips`, { params: { region: regionId } })
  }

  /**
   * List all available branches for a given git
   * @param region 
   */
  public listPublicBranchesFromRepository(url: string, workspaceId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.endpoint}/utils/git`, { params: { workspace: workspaceId, url } })
  }
  
  /**
   * Helper to validate if registry is reachable
   * @param url 
   * @returns 
   */
  public listPublicRegistry(url: string, workspaceId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.endpoint}/utils/reg`, { params: { workspace: workspaceId, registry_url: url } })
  }
  
  /**
   * Helper to validate if registry is reachable
   * @param url 
   * @returns 
   */
  public listPrivateRegistry(url: string, sid: string, workspaceId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.endpoint}/utils/reg`, { params: { workspace: workspaceId, registry_url: url, id: sid } })
  }
  
  /**
   * List all available branches for a privat git
   * @param url 
  */
  public listPrivateBranchesFromRepository(url: string, sid: string, workspaceId: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.endpoint}/utils/git`, { params: { workspace: workspaceId, url, id: sid } })
  }

  public probeHealthCheck(url: string, workspaceId: string): Observable<{ status: number | null; ok: boolean; error: boolean; attempts: number }> {
    return this.http.get<{ status: number | null; ok: boolean; error: boolean; attempts: number }>(`${this.endpoint}/utils/health-check`, { params: { workspace: workspaceId, health_check_path: url } })
  }
}
